import React, { useMemo } from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import { InputAdornment, Button } from "@mui/material";
import { Search, Add } from "@mui/icons-material";
import BillingDataTable from "./components/BillingDataTable";
import { useNavigate } from "react-router-dom";
import pathnames from "../../constants/pathnames";
import organizationservice from "../../service/organizationservice";
import { useQuery } from "@tanstack/react-query";
import PreloaderTable from "../../lib/components/PreloaderTable";
import useDebounce from "../../hooks/useDebounce";

const Billing = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "10";
  const keyword = searchParams.get("keyword") || "";

  const searchTerm = useDebounce(keyword);

  const plansQuery = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: async () => {
      const res = await organizationservice.getSubscriptionPlans();
      return res.data || [];
    },
    staleTime: 60 * 1000,
  });

  const handleEdit = (row) => {
    navigate(pathnames.BILLING_ADD_NEW, {
      state: { plan: row.__raw },
    });
  };

  const tableData = (plansQuery.data?.data || []).map((plan) => {
    const firstPricing = Object.values(plan.pricing || {}).find((p) => Number(p.amount) > 0) || {};

    return {
      id: firstPricing.id || plan.id || plan._id,
      name: plan.name,
      description: plan.description || "-",
      price: firstPricing.amount ? `₦${Number(firstPricing.amount).toLocaleString()}/${firstPricing.interval?.toLowerCase()}` : "-",
      isActive: Boolean(plan.status) || false,
      __raw: plan,
    };
  });

  const filteredData = useMemo(() => {
    if (!searchTerm) return tableData;
    return tableData.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [tableData, searchTerm]);

  const totalPages = Math.ceil(filteredData.length / Number(limit));

  const paginatedData = useMemo(() => {
    const start = (Number(page) - 1) * Number(limit);
    return filteredData.slice(start, start + Number(limit));
  }, [filteredData, page, limit]);

  const refreshPlans = () => plansQuery.refetch();

  function handleChange(e, name = "") {
    const value = name === "page" ? e : e.target.value;
    searchParams.set(name, value);
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  }

  return (
    <AppLayout>
      <Header title="Billing Plans" description="Manage, edit or create new billings and subscription here" />

      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        {/* SEARCH + ADD BUTTON (FIXED ONLY HERE) */}
        <div className="row mb-3 align-items-center">
          <div className="col-12 col-md-4 col-lg-3 mb-3 mb-md-0">
            <AppTextField
              placeholder="Enter Text Here..."
              value={keyword}
              onChange={(e) => handleChange(e, "keyword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="col-12 col-md-8 col-lg-9 d-flex justify-content-end">
            <Button
              variant="contained"
              color="primary"
              endIcon={<Add />}
              onClick={() => navigate(pathnames.BILLING_ADD_NEW)}
              style={{
                backgroundColor: "#3366FF",
                color: "#fff",
                textTransform: "none",
                padding: "8px 20px",
              }}
            >
              Add New Plan
            </Button>
          </div>
        </div>

        <PreloaderTable isLoading={plansQuery.isLoading} />

        <BillingDataTable
          data={paginatedData}
          isLoading={plansQuery.isLoading}
          page={Number(page)}
          pageSize={Number(limit)}
          totalPages={totalPages}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) => handleChange({ target: { value } }, "limit")}
          onToggleStatus={refreshPlans}
          onEdit={handleEdit}
        />

        {plansQuery.isError && <div className="text-danger p-4">Failed to fetch plans</div>}
      </div>
    </AppLayout>
  );
};

export default Billing;
